import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Stack, Typography, List, ListItemButton, InputBase, Skeleton, ButtonBase, IconButton, Tooltip, Select, MenuItem} from '@mui/material';
import {useAtom, useAtomValue} from 'jotai';
import {useNavigate} from 'react-router-dom';
import {HugeiconsIcon} from '@hugeicons/react';
import {Search01Icon, DatabaseIcon, Add01Icon} from '@hugeicons/core-free-icons';
import {SubProjectOption} from '../../SubProjectSelect';
import {KnowledgeManagerRepository} from '../../../../repositories/KnowledgeManagerRepository';
import {useStompWebsocketKbManagerMulti} from '../../../../hooks/useStompWebsocketKbManagerMulti';
import {
  topicsAtom, selectedTopicAtom, topicSearchAtom, topicLanguageFilterAtom, topicsLoadingAtom, projectAtom,
} from '../atoms';

const controlSx = {height: 36, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};

const hexToRgba = (hex, alpha) => {
  const clean = (hex || "#8491F7").replace("#", "");
  const full = clean.length === 3 ? clean.split("").map(c => c + c).join("") : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const TopicListPanel = ({onAddClick}) => {
  const [topics] = useAtom(topicsAtom);
  const [selectedTopic, setSelectedTopic] = useAtom(selectedTopicAtom);
  const [search, setSearch] = useAtom(topicSearchAtom);
  const [languageFilter, setLanguageFilter] = useAtom(topicLanguageFilterAtom);
  const [loading] = useAtom(topicsLoadingAtom);
  const project = useAtomValue(projectAtom);
  const navigate = useNavigate();

  const subProjects = project?.subProjects || [];
  const activeLanguage = subProjects.find(item => String(item.id) === languageFilter);

  // Real per-document status, not just "has documents" - the topics list only carries a raw
  // documentCount, so a true indexed/failed/processing breakdown needs each topic's actual document
  // statuses. Tracked per-document ({topicId: {docId: status}}) rather than as aggregate counts so
  // live WS pushes can overwrite a single document's status idempotently without double-counting.
  const [docsByTopic, setDocsByTopic] = useState({});

  // Baseline REST fetch - but only for topics whose known document count doesn't match the topics
  // list's documentCount (i.e. a document was actually added/removed), not on every topics refresh.
  // Status *transitions* (processing -> indexed/failed) on documents we already know about arrive
  // live over the WS subscription below instead, with no REST call at all.
  useEffect(() => {
    const stale = topics.filter(t => {
      const known = docsByTopic[t.id];
      return (known ? Object.keys(known).length : -1) !== (t.documentCount || 0);
    });
    if (stale.length === 0) return;
    let cancelled = false;
    Promise.all(stale.map(t => KnowledgeManagerRepository.listDocuments(t.id)
      .then(docs => [t.id, Object.fromEntries(docs.map(d => [d.id, d.status]))])
      .catch(() => [t.id, null])))
      .then(entries => {
        if (cancelled) return;
        setDocsByTopic(prev => {
          const next = {...prev};
          entries.forEach(([id, statuses]) => {if (statuses) next[id] = statuses;});
          return next;
        });
      });
    return () => {cancelled = true;};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topics]);

  // Live status pushes for every topic, over a single shared STOMP connection - keeps the health
  // widget accurate as documents move processing -> indexed/failed without re-polling REST.
  const topicDestinations = useMemo(() => topics.map(t => `/topic/topics/${t.id}/documents`), [topics]);
  const handleDocStatusPush = useCallback((update) => {
    const doc = update?.document;
    if (!doc?.topicId || !doc.id || !doc.status) return;
    setDocsByTopic(prev => ({...prev, [doc.topicId]: {...prev[doc.topicId], [doc.id]: doc.status}}));
  }, []);
  useStompWebsocketKbManagerMulti(topicDestinations, handleDocStatusPush);

  // "All sub projects" (value "") just filters in place - there's no route for it. Picking a specific
  // sub project also navigates there, same as the MCP registry's sub project switcher.
  const handleLanguageChange = (value) => {
    setLanguageFilter(value);
    if (!value || !project?.id) return;
    const target = subProjects.find(item => String(item.id) === value);
    if (target) navigate(`/registry-project/${project.id}/${target.id}/knowledge-manager`);
  };

  const languageName = (subProjectId) => {
    if (!subProjectId) return null;
    const match = (project?.subProjects || []).find(item => String(item.id) === String(subProjectId));
    return match?.languageCode || match?.languageName || null;
  };

  const filteredTopics = useMemo(() => topics.filter(topic => {
    const matchesSearch = topic.name?.toLowerCase().includes(search.toLowerCase())
      || topic.description?.toLowerCase().includes(search.toLowerCase());
    const matchesLanguage = !languageFilter || String(topic.subProjectId) === languageFilter;
    return matchesSearch && matchesLanguage;
  }), [topics, search, languageFilter]);

  return (
    <Stack sx={{height: '100%', minHeight: 0, p: 3, gap: 2.5, display: 'flex', flexDirection: 'column'}}>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography noWrap sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8A8AA0", textTransform: "uppercase"}}>
            All topics
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.75} sx={{flexShrink: 0}}>
            {subProjects.length > 0 && (
              <Select
                variant="standard"
                disableUnderline
                displayEmpty
                value={languageFilter}
                onChange={(e) => handleLanguageChange(e.target.value)}
                renderValue={() => activeLanguage
                  ? <SubProjectOption subProject={activeLanguage}/>
                  : <Typography noWrap sx={{fontSize: 12, fontWeight: 600, color: "#8A8AA0"}}>All sub projects</Typography>}
                inputProps={{"aria-label": "Filter by language"}}
                sx={{
                  maxWidth: 140, borderRadius: "999px", px: 1.25,
                  color: activeLanguage ? "#4B55C9" : "#8A8AA0",
                  bgcolor: activeLanguage ? "rgba(132, 145, 247, 0.12)" : "#F2F2F7",
                  "& .MuiSelect-select": {py: 0.5, pr: "22px !important", display: "flex", alignItems: "center"},
                  "& .MuiSelect-icon": {color: activeLanguage ? "#4B55C9" : "#8A8AA0"},
                }}
                MenuProps={{PaperProps: {sx: {borderRadius: "12px", mt: 0.5}}}}
              >
                <MenuItem value={""} dense>
                  <Typography sx={{fontSize: 12, fontWeight: 600, color: "#8A8AA0"}}>All sub projects</Typography>
                </MenuItem>
                {subProjects.map(item => (
                  <MenuItem key={item.id} value={String(item.id)} dense>
                    <SubProjectOption subProject={item}/>
                  </MenuItem>
                ))}
              </Select>
            )}
            <Tooltip title={"New topic"}>
              <IconButton
                aria-label={"Add topic"} onClick={onAddClick}
                sx={{width: 34, height: 34, borderRadius: '10px', border: '1px solid #ECECF3', color: '#6B6B80', bgcolor: '#fff', '&:hover': {bgcolor: '#FAFAFD'}}}
              >
                <HugeiconsIcon icon={Add01Icon} size={16}/>
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
        <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", mt: 0.5}}>
          {topics.length} knowledge area{topics.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      <Box sx={{...controlSx}}>
        <HugeiconsIcon icon={Search01Icon} size={16}/>
        <InputBase placeholder="Search topics..." value={search} onChange={(e) => setSearch(e.target.value)}
                   inputProps={{"aria-label": "Search topics"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}/>
      </Box>

      <Box sx={{flex: 1, minHeight: 0, overflow: 'auto', mr: -1, pr: 1}}>
        {loading && topics.length === 0 ? (
          <Stack gap={1}>
            {Array.from({length: 5}).map((_, index) => (
              <Box key={index} sx={{p: 1.25, borderRadius: '10px'}}>
                <Skeleton height={18} width="60%" sx={{borderRadius: '6px'}}/>
                <Skeleton height={14} width="85%" sx={{borderRadius: '6px', mt: 0.5}}/>
              </Box>
            ))}
          </Stack>
        ) : filteredTopics.length > 0 ? (
          <List sx={{p: 0, gap: 1, display: 'flex', flexDirection: 'column'}}>
            {filteredTopics.map((topic) => {
              const isSelected = selectedTopic?.id === topic.id;
              return (
              <ListItemButton
                key={topic.id}
                selected={isSelected}
                onClick={() => setSelectedTopic(topic)}
                sx={{
                  p: 1.5, borderRadius: '12px', border: '1px solid transparent', transition: 'background-color 0.2s ease, border-color 0.2s ease',
                  backgroundColor: isSelected ? hexToRgba(topic.color, 0.08) : 'transparent',
                  borderColor: isSelected ? hexToRgba(topic.color, 0.35) : 'transparent',
                  '&:hover': {backgroundColor: isSelected ? hexToRgba(topic.color, 0.12) : '#F7F7FA'},
                  '&.Mui-selected': {backgroundColor: hexToRgba(topic.color, 0.08), borderColor: hexToRgba(topic.color, 0.35)},
                  '&.Mui-selected:hover': {backgroundColor: hexToRgba(topic.color, 0.12)},
                }}
              >
                <Box sx={{width: '100%', minWidth: 0}}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: topic.color || "#8491F7"}}/>
                    <Typography sx={{fontWeight: 700, fontSize: '14px', color: '#23264D', flex: 1, minWidth: 0}} noWrap>
                      {topic.name}
                    </Typography>
                  </Stack>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{mt: 0.5}}>
                    <Typography sx={{fontSize: 12, color: "#8A8AA0", minWidth: 0}} noWrap>
                      {topic.documentCount || 0} docs · {topic.queryCount || 0} queries
                      {topic.tags?.length > 0 && ` · ${topic.tags.slice(0, 3).join(', ')}${topic.tags.length > 3 ? ` +${topic.tags.length - 3}` : ''}`}
                    </Typography>
                    {languageName(topic.subProjectId) && (
                      <Typography sx={{fontSize: 11, fontWeight: 700, color: '#6B6B80', textTransform: 'uppercase', flexShrink: 0}}>
                        {languageName(topic.subProjectId)}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </ListItemButton>
              );
            })}
          </List>
        ) : (
          <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6, px: 2, textAlign: 'center'}}>
            <Box sx={{
              width: 48, height: 48, borderRadius: '14px', display: 'flex', alignItems: 'center',
              justifyContent: 'center', bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#8491F7',
            }}>
              <HugeiconsIcon icon={topics.length === 0 ? DatabaseIcon : Search01Icon} size={22}/>
            </Box>
            <Box>
              <Typography sx={{fontSize: 14, fontWeight: 700, color: '#23264D'}}>
                {topics.length === 0 ? 'No topics yet' : 'No topics match'}
              </Typography>
              <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.5}}>
                {topics.length === 0 ? 'Create a topic to start building a knowledge base' : 'Try a different search or filter'}
              </Typography>
            </Box>
            {topics.length === 0 && (
              <ButtonBase onClick={onAddClick} sx={{
                mt: 0.5, px: 1.5, py: 0.75, borderRadius: '10px', fontSize: 12, fontWeight: 700,
                color: '#4B55C9', bgcolor: 'rgba(132, 145, 247, 0.1)', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.16)'},
              }}>
                Create your first topic
              </ButtonBase>
            )}
          </Box>
        )}
      </Box>

      {topics.length > 0 && (() => {
        const stillLoading = topics.some(t => !docsByTopic[t.id]);
        const allStatuses = Object.values(docsByTopic).flatMap(byDoc => Object.values(byDoc));
        const totalDocs = allStatuses.length;
        const totalIndexed = allStatuses.filter(s => s === "indexed").length;
        const totalFailed = allStatuses.filter(s => s === "failed").length;
        const healthPct = totalDocs > 0 ? Math.round((totalIndexed / totalDocs) * 100) : 0;
        const barColor = totalFailed > 0 ? '#DC2626' : healthPct === 100 ? '#2F9A6A' : '#D9A13A';

        return (
          <Box sx={{borderRadius: '12px', bgcolor: '#FAFAFD', p: 1.5, flexShrink: 0}}>
            <Stack direction="row" alignItems="center" gap={0.75} sx={{mb: 1}}>
              <HugeiconsIcon icon={DatabaseIcon} size={14} color={"#23264D"}/>
              <Typography sx={{fontSize: 12, fontWeight: 700, color: '#23264D'}}>Index health</Typography>
            </Stack>
            {stillLoading && totalDocs === 0 ? (
              <Skeleton height={6} sx={{borderRadius: '999px'}}/>
            ) : totalDocs === 0 ? (
              <Typography sx={{fontSize: 11, color: '#8A8AA0'}}>No documents uploaded yet</Typography>
            ) : (
              <>
                <Box sx={{height: 6, borderRadius: '999px', bgcolor: '#ECECF3', overflow: 'hidden'}}>
                  <Box sx={{height: '100%', width: `${healthPct}%`, borderRadius: '999px', bgcolor: barColor, transition: 'width 0.3s ease'}}/>
                </Box>
                <Stack direction="row" justifyContent="space-between" sx={{mt: 0.75}}>
                  <Typography sx={{fontSize: 11, color: '#8A8AA0'}}>
                    {totalFailed > 0
                      ? `${totalFailed} failed · ${totalIndexed}/${totalDocs} indexed`
                      : healthPct === 100 ? 'All documents indexed' : `${totalIndexed}/${totalDocs} documents indexed`}
                  </Typography>
                  <Typography sx={{fontSize: 11, fontWeight: 700, color: '#6B6B80'}}>{healthPct}%</Typography>
                </Stack>
              </>
            )}
          </Box>
        );
      })()}
    </Stack>
  );
};
